import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios"
import {Config} from "../config/config";
import {sessionService} from "./session.service";

class ApiClientService {
    constructor() {
        // Basis-Konfiguration sofort setzen
        axios.defaults.baseURL = ''; // Entferne den baseURL prefix
        axios.defaults.timeout = Config.timeout;
        axios.defaults.withCredentials = true;
        
        // Interceptors sofort einrichten
        this.setupInterceptors();
    }
    private setupInterceptors() {
        // Request Interceptor
        axios.interceptors.request.use(request => {
            // Debug vor der URL-Anpassung
            console.log('[API-DEBUG] Request before URL adjustment:', {
                originalUrl: request.url,
                baseURL: axios.defaults.baseURL,
                headers: request.headers
            });

            // Stelle sicher, dass die URL korrekt formatiert ist
            if (request.url && Config.apiRoot && !request.url.includes('://')) {
                const baseUrl = Config.apiRoot;
                let apiPath = request.url;
                
                // Normalisiere den Pfad
                if (apiPath.startsWith('api/')) {
                    apiPath = `/${apiPath}`;
                }
                
                // Behandle /api Präfix
                const baseUrlHasApi = baseUrl.endsWith('/api');
                if (baseUrlHasApi && (apiPath.startsWith('/api/') || apiPath === '/api')) {
                    apiPath = apiPath.replace(/^\/api/, '');
                } else if (!baseUrlHasApi && !apiPath.startsWith('/api/')) {
                    apiPath = `/api/${apiPath.replace(/^\//, '')}`;
                }
                
                // URL zusammensetzen
                request.url = `${baseUrl}${apiPath}`;
                
                console.log('[API-DEBUG] Request after URL adjustment:', {
                    newUrl: request.url,
                    headers: request.headers
                });
            }

            console.log('[API-DEBUG] Starting Request:', {
                url: request.url,
                method: request.method,
                data: request.data,
                headers: request.headers,
                baseURL: axios.defaults.baseURL,
                withCredentials: request.withCredentials
            });

            return request;
        }, error => {
            console.error('[API-DEBUG] Request Error:', {
                error: error.message,
                config: error.config,
                baseURL: axios.defaults.baseURL
            });
            return Promise.reject(error);
        });

        // Response Interceptor
        axios.interceptors.response.use(response => {
            console.log('[API-DEBUG] Response:', {
                status: response.status,
                url: response.config.url,
                headers: response.headers,
                data: response.data
            });
            return response;
        }, error => {
            // Ausführliche Fehlerbehandlung
            if (error.response) {
                // Server antwortete mit einem Status-Code außerhalb des 2xx-Bereichs
                console.error('[API-ERROR] Response error:', {
                    status: error.response.status,
                    url: error.config?.url,
                    method: error.config?.method,
                    data: error.response.data,
                    headers: error.response.headers
                });
            } else if (error.request) {
                // Anfrage wurde gesendet, aber keine Antwort erhalten
                console.error('[API-ERROR] Network error (no response):', {
                    url: error.config?.url,
                    method: error.config?.method,
                    error: error.message,
                    request: {
                        status: error.request.status,
                        responseURL: error.request.responseURL,
                        timeout: error.config?.timeout
                    }
                });
            } else {
                // Etwas ist beim Einrichten der Anfrage schief gelaufen
                console.error('[API-ERROR] Request setup error:', {
                    error: error.message,
                    config: error.config
                });
            }
            
            return Promise.reject(error);
        });
    }

    async configureClient() {
        try {
            console.log('Configuring API client...', {
                timeout: Config.timeout,
                apiRoot: Config.apiRoot
            });

            // Versuche Session zu validieren
            const session = await sessionService.validate().catch(error => {
                console.log('Session validation failed (expected for new sessions):', error);
                return null;
            });

            // Token nur setzen, wenn eine Session existiert
            if (session?.token) {
                axios.defaults.headers.common['Authorization'] = `Bearer ${session.token}`;
                console.log('Session token set');
            } else {
                console.log('No session token available');
            }

        } catch (error) {
            console.error('Error during API client configuration:', error);
            // Fehler nicht werfen, damit die App trotzdem startet
        }
    }

    onResponse(response: AxiosResponse) {
        return response;
    }

    onResponseError(error: AxiosError) {
        if (error?.response?.status === 429) {
            sessionService.validate();
        }
        return Promise.reject(error);
    }
}

export default new ApiClientService();